import { LMS_CONSTANTS } from "@ipru-common/constants/SiteConstants";
import { MARITAL_STATUS, OPTION_VALUES, PaymentOptionName } from "@ipru-common/constants/InputValues";
import CryptoJS from "crypto-js";
import _ from "lodash";
import GlobalStore from "@ipru-common/store/GlobalStore";
import { calculatePayoutDetails, getCiTerm } from "../QuoteCommonFunctions";
import CommonKeys from "./CommonUserInput";
import { getCurrentTimeStamp } from "../SavingsAppForm/SavingAppFormsUtils";
import { findLifeCoverPremium } from "../IPSLifeCoverPremiumCalculations";
import RatesProviderStore from "@ipru-common/store/RatesProviderStore";
import { getAge } from "@ipru-common/libs/ValidationSchemas/CustomRules";
import { currencyToWord } from "../CommonFunctions";

const getIPSUserInput = () => {
  const {
    quoteFormData,
    calculatorFormData,
    existingCustomer,
    currentPageStep,
    appFormData,
    spouseCover,
    SPOUSE_COVER,
  } = GlobalStore.getSnapshot();

  const ratesInContext = RatesProviderStore.getSnapshot();

  const urlParams = new URLSearchParams(window?.location?.search);
  const params = Object.fromEntries(urlParams?.entries());

  const { lumpsumPercentage, payoutTerm } = calculatePayoutDetails(
    quoteFormData?.payoutType,
    quoteFormData?.monthlyIncomePayoutYears,
    quoteFormData?.SA,
    quoteFormData?.monthlyIncomeAmt,
    quoteFormData?.lumpSumAndIncomeYears,
    quoteFormData?.lumpSumIncomeSingleInst,
    quoteFormData?.income_duration
  );

  const getSuggestedPremium = () => {
    const finalPremiums = findLifeCoverPremium({
      formData: {
        ...calculatorFormData,
        ...quoteFormData,
        SA: 15000000,
        channel: OPTION_VALUES.BOL,
      },
      rates: ratesInContext,
      platform: OPTION_VALUES.ONLINE,
      paymentOptions: [{ ppt: quoteFormData?.ppt, key: quoteFormData?.paymentOption }],
    });
    const { tenure, paymentOption, planType } = quoteFormData || {};
    const onlinePremium = finalPremiums?.[paymentOption]?.[tenure];
    return onlinePremium?.[`${planType}`];
  };

  const getAppFormStep = () => {
    let result = 1;
    if (!appFormData?.appNO) {
      result = 1;
    } else {
      if (appFormData?.stepsCleared?.personalDetails && !appFormData?.stepsCleared?.nomineeDetails) {
        result = 2;
      } else if (appFormData?.stepsCleared?.nomineeDetails && !appFormData?.stepsCleared?.healthDetails) {
        result = 3;
      } else if (appFormData?.stepsCleared?.healthDetails) {
        result = 4;
      }
    }
    return result;
  };

  const getIsPptRecommendedSelected = () => {
    if (currentPageStep != "appFormScreen") return quoteFormData?.isPPTChanged ? "t" : "f";
    else return quoteFormData?.isPPTChanged ? "TRUE" : "FALSE";
  };

  const suggestedPremium = getSuggestedPremium();

  const getSpouseKeys = () => {
    if (spouseCover) {
      return {
        spousename: SPOUSE_COVER?.appFormData?.formWizard?.personalDetails?.full_name,
        TaggedAppPrem: SPOUSE_COVER?.quoteFormData?.breakupDetail?.total,
      };
    }
    return {};
  };

  let ipsUserInputs = {
    landmark: appFormData?.formWizard?.personalDetails?.landmark,
    dobspouse: "",
    pannumber: appFormData?.formWizard?.personalDetails?.pan_number,
    occ: appFormData?.formWizard?.occupationDetails?.my_profession,
    pin: appFormData?.formWizard?.occupationDetails?.pincode,
    SpouseEducation: OPTION_VALUES.EDUCATION.GRADUATE,
    SpouseOccupation: OPTION_VALUES.OCCUPATION.HOUSEWIFE,
    SpouseTobacco: OPTION_VALUES.N_NO,
    useTobacco: calculatorFormData?.tobacco,
    channel: quoteFormData?.channel,
    currentPageStep: currentPageStep,
    CIterm:
      getCiTerm(quoteFormData?.paymentOption, calculatorFormData?.age, quoteFormData?.ppt, quoteFormData?.term) || 0,
    ADBterm: _.min([quoteFormData?.term, 80 - parseInt(calculatorFormData?.age)]) || 0,
    premiumPaymentOption: PaymentOptionName[quoteFormData?.paymentOption],
    sumAssured: quoteFormData?.lumpSumAmt,
    sumAssured2: currencyToWord(quoteFormData?.lumpSumAmt),
    Accidental_Death_Benefit: quoteFormData?.ADB_SA,
    Critical_Illness_Benefit: quoteFormData?.CI_SA,
    PayoutOptions: LMS_CONSTANTS[quoteFormData?.payoutType],
    PaymentFrequency: LMS_CONSTANTS[quoteFormData?.tenure],
    Plan: `${quoteFormData?.planType?.charAt(0).toUpperCase()}${quoteFormData?.planType?.slice(1)}`,
    LumpsumPer: lumpsumPercentage == 0 ? 100 : lumpsumPercentage,
    IncomePer: 50,
    TRAI_FLAG: calculatorFormData?.trai_flag ? OPTION_VALUES.Y_YES : OPTION_VALUES.N_NO,
    TRAI_FLAG_SM: OPTION_VALUES.N_NO,
    lead_type: currentPageStep != "appFormScreen" ? "Lead" : "Drop",
    Page_Name: LMS_CONSTANTS.IPS_PAGE_NAME,
    clientId: "",
    Premium: quoteFormData?.breakupDetail?.total,
    employeeId: calculatorFormData?.employeeId,
    yearly_premium: quoteFormData?.breakupDetail?.annualPremium,
    annual_income: calculatorFormData?.yr_income,
    nominee: appFormData?.formWizard?.nomineeDetails,
    timestamp: getCurrentTimeStamp(),
    monthly_premium: LMS_CONSTANTS[quoteFormData?.tenure] === "monthly" && quoteFormData?.breakupDetail?.total,
    next_year_premium: quoteFormData?.breakupDetail?.onSecondYearPremium,
    next_year_monthly_premium: quoteFormData?.breakupDetail?.total,
    next_year_yearly_premium: quoteFormData?.breakupDetail?.annualPremium,
    suggested_cover: quoteFormData?.recommended,
    annualincome_multplier: quoteFormData?.income_duration,
    premiumwithci: quoteFormData?.breakupDetail?.base + quoteFormData?.breakupDetail?.ciPremium,
    suggestedpremium: suggestedPremium,
    suggestedpremiumdiff: suggestedPremium - quoteFormData?.breakupDetail?.total,
    existing_cust: calculatorFormData?.employeeId || existingCustomer ? "Yes" : "No",
    fullname: appFormData?.formWizard?.personalDetails?.full_name || "f",
    flag_partner: calculatorFormData?.pasa || "f",
    isnri_vmer: "f",
    ishousewife: calculatorFormData.calc_occupation === OPTION_VALUES.OCCUPATION.HOUSEWIFE ? "t" : "f",
    isppt_recomd_selected: getIsPptRecommendedSelected(),
    appno: appFormData?.appNO,
    maritalstatus: MARITAL_STATUS[appFormData?.formWizard?.personalDetails?.marital_status],
    countrycode: calculatorFormData?.country_code,
    countryname: calculatorFormData?.country_of_residence,
    persona: params?.persona,
    h_mobile: CryptoJS.MD5(calculatorFormData?.mobileno).toString().toUpperCase(),
    h_email: CryptoJS.MD5(calculatorFormData?.emailid).toString().toUpperCase(),
    whatsapp_optin: quoteFormData?.wth_option ? "Yes" : "No",
    Step: currentPageStep != "appFormScreen" ? "step1" : `step${getAppFormStep()}`,
    Education: calculatorFormData?.calc_education,
    State: calculatorFormData?.state,
    pincodeLead: OPTION_VALUES.YES,
    Nri: calculatorFormData?.nri,
    youth_f_name: "",
    youth_l_name: "",
    youth_pan_no: "",
    youth_address: "",
    youthResult: "",
    youthToken: "",
    YouthJourney: OPTION_VALUES.N_NO,
    nriFullName: "",
    nriMoblieno: calculatorFormData?.mobileno,
    nriEmail: "",
    livingInIndia: calculatorFormData?.is_nri_in_india ? OPTION_VALUES.NRI_YES : OPTION_VALUES.NRI_NO,
    nriArrivingDate: "",
    exitpopup: calculatorFormData?.pri_frst_nm?.length > 0 ? "t" : "f",
    InpayOutTerm: payoutTerm,
    LumInpayOutTerm: payoutTerm,
    origin: "",
    premiumWithoutQuote: "f",
    ipsLeadDynamicSA: {
      minSA: "",
      maxSA: "",
    },
    diabetes: quoteFormData?.isDiabetic === 1 ? OPTION_VALUES.Y_YES : OPTION_VALUES.N_NO,
    isEnglish: params?.lan === "hindi" ? "FALSE" : "TRUE",
    husbandPopup: {
      dob: calculatorFormData?.husbandDetail?.husband_dob,
      age: calculatorFormData?.husbandDetail?.husband_dob && getAge(calculatorFormData?.husbandDetail?.husband_dob),
      AnnualIncome: calculatorFormData?.husbandDetail?.husband_yr_income || "",
      Occupation: calculatorFormData?.husbandDetail?.husband_occupation || "",
      Education: calculatorFormData?.husbandDetail?.husband_education || "",
      HasLifeCover: OPTION_VALUES.Y_YES,
    },
    PPT: quoteFormData?.ppt,
    ...getSpouseKeys(),
  };

  return { ...CommonKeys(calculatorFormData, existingCustomer, quoteFormData), ...ipsUserInputs };
};

export default getIPSUserInput;
